import { sendNewUserProfileImage } from "@/models/users.model";
import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import { Card } from "@/shadcn/components/ui/card";
import {
  createThumbnail,
  genericFailureToast,
  genericSuccessToast,
} from "@/utils/utils";
import { User } from "lucide-react";
import React, { useContext, useState } from "react";

type UserProfileViewProps = {
  // TODO: add props here
};

const UserProfileView: React.FC<UserProfileViewProps> = (props) => {
  const { user, initialGetAllAppUsers } = useContext(AppContext);

  const [userImage, setUserImage] = useState<string>(user.profile_picture);

  async function handleImageSelection() {
    try {
      // Create a file input dynamically to open the file dialog
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.multiple = false;

      input.onchange = async (event: Event) => {
        const target = event.target as HTMLInputElement;
        const file = target.files ? target.files[0] : null; // Get the first file
        if (!file) return;

        const thumbnail = await createThumbnail(file, 80); // Create a thumbnail from the image

        if (!thumbnail) {
          genericFailureToast("Failed to create thumbnail"); // Show error toast if thumbnail creation fails
          return;
        }

        setUserImage(thumbnail); // Set the thumbnail as the user image
      };

      // Trigger the file input click event to open the file dialog
      input.click();
    } catch (error) {
      console.error("Error selecting image:", error); // Log any errors that occur during image selection
      genericFailureToast("Failed to select image"); // Show error toast if image selection fails
    }
  }

  async function handleImageUpload() {
    // sendNewUserProfileImage

    if (!userImage) return; // If no image, return

    const response = await sendNewUserProfileImage(userImage); // Send the new user profile image to the server

    if (response.rStatus === "success") {
      await initialGetAllAppUsers(true);
      genericSuccessToast("Image uploaded successfully"); // Show success toast if upload is successful
    } else {
      // Handle error response
      genericFailureToast("Failed to upload image"); // Show error toast if upload fails
    }
  }

  return (
    <div className="h-full w-full p-4">
      <Card className="rounded-sm w-auto m-auto h-auto max-w-[700px] bg-accent">
        <div className="flex flex-col items-center justify-center p-4">
          <div className="flex items-center justify-center mt-2">
            <span className="cursor-pointer" onClick={handleImageSelection}>
              {userImage ? (
                <img
                  src={userImage}
                  alt="User Profile"
                  className="rounded-full w-16 h-16"
                />
              ) : (
                <User className="w-16 h-16 text-gray-500 rounded-full shadow-md" />
              )}
            </span>
          </div>
          <h1 className="text-xl font-bold mt-4">User Profile</h1>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col items-start justify-start mt-4">
              <label className="text-sm font-semibold">First Name</label>
              <p className="text-sm">{user.first_name}</p>
            </div>
            <div className="flex flex-col items-start justify-start mt-4">
              <label className="text-sm font-semibold">Surname</label>
              <p className="text-sm">{user.surname}</p>
            </div>
            <div className="flex flex-col items-start justify-start mt-4">
              <label className="text-sm font-semibold">Email</label>
              <p className="text-sm">{user.email}</p>
            </div>
            <div className="flex flex-col items-start justify-start mt-4">
              <label className="text-sm font-semibold">Timezone</label>
              <p className="text-sm">{user.timezone}</p>
            </div>
            <div className="flex flex-col items-start justify-start mt-4">
              <label className="text-sm font-semibold">Role</label>
              <p className="text-sm">{user.role_name}</p>
            </div>
          </div>
          {userImage !== user.profile_picture && (
            <div className="flex justify-end justify-items-end w-full">
              <Button className="mt-4" onClick={handleImageUpload}>
                Save
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default UserProfileView;
